<template>
    <transition name="fade">
        <div class="popover" v-if="isOpen" :style="popoverStyle">
            <div class="popover-arrow" :style="arrowStyle" ref="arrow"/>
            <slot/>
        </div>
    </transition>
</template>

<script>
    import {addClickOutsideHandler, removeClickOutsideHandler, matchesSelector} from '../helper';

    export default {
        props: {
            target: {type: [Object, HTMLElement, SVGCircleElement, SVGRectElement], default: null},
            direction: {type: String, default: 'auto'},
            alignRight: {type: Boolean, default: false},
            autoBind: {type: Boolean, default: false},
            autoCloseExcept: {default: () => [], type: Array},
            offsetX: {type: Number, default: 0},
            offsetY: {type: Number, default: 0},
            offsetArrow: {type: Number, default: null},
        },

        data() {
            return {
                isOpen: false,
                popoverStyle: {},
                arrowStyle: {},
            };
        },

        computed: {
            targetNode() {
                if (this.target instanceof Object || this.target instanceof HTMLElement || this.target instanceof SVGElement) {
                    return this.target.hasOwnProperty('$el') ? this.target.$el : this.target;
                }
                return this.$el.previousElementSibling || this.$el.previousSibling || null;
            },
        },


        methods: {
            toggle(open = null) {
                if (open === null || open instanceof Event) {
                    open = !this.isOpen;
                }
                this.isOpen = open;
            },

            open() {
                this.isOpen = true;
            },

            close() {
                this.isOpen = false;
            },

            updatePosition() {
                if (!this.targetNode) {
                    console.warn('Missing target node for popover');
                    return;
                }

                let popover = this.$el;

                if (!(popover instanceof Element && this.$refs.arrow instanceof Element)) {
                    return;
                }

                let popoverRect = popover.getBoundingClientRect();
                let targetRect = this.targetNode.getBoundingClientRect();
                let arrowRect = this.$refs.arrow.getBoundingClientRect();
                let direction = this.direction;

                // auto detect direction
                if (direction === 'auto') {
                    let toBottom = window.innerHeight - targetRect.height - targetRect.top;
                    direction = targetRect.top > toBottom ? 'up' : 'down';
                }

                let top = targetRect.top;
                let left = targetRect.left;
                let arrowStyle = {};

                if (!this.$ui.ie11) {
                    let offset = this.calculateOffset();
                    top -=  offset.top;
                    left -=  offset.left;
                }

                switch (direction) {
                    case 'down':
                        top = top + targetRect.height + this.offsetY + (arrowRect.height / 2);
                        arrowStyle.top = -arrowRect.width + 'px';
                        break;
                    case 'up':
                        top = top - popoverRect.height - this.offsetY - (arrowRect.height / 2);
                        arrowStyle.bottom = -arrowRect.width + 'px';
                        arrowStyle.transform = 'rotate(180deg)';
                        break;
                    case 'right':
                        left = left + targetRect.width + this.offsetX + (arrowRect.width / 2);
                        arrowStyle.left = -arrowRect.width + 'px';
                        arrowStyle.transform = 'rotate(-90deg)';
                        break;
                    case 'left':
                        left = left - popoverRect.width - this.offsetX - (arrowRect.width / 2);
                        arrowStyle.right = -arrowRect.width + 'px';
                        arrowStyle.transform = 'rotate(90deg)';
                        break;
                }

                let arrowOffset = this.offsetArrow;
                if (['down', 'up'].includes(direction)) {
                    if (arrowOffset === null) {
                        arrowOffset = (Math.min(targetRect.width, popoverRect.width) - arrowRect.width) / 2;
                    }
                    if (this.alignRight) {
                        left = targetRect.left + targetRect.width - popoverRect.width - this.offsetX;
                        arrowStyle.right = arrowOffset + 'px';
                    } else {
                        left += this.offsetX;
                        arrowStyle.left = arrowOffset + 'px';
                    }
                } else {
                    if (arrowOffset === null) {
                        arrowOffset = (Math.min(targetRect.height, popoverRect.height) - arrowRect.height) / 2;
                    }
                    arrowStyle.top = arrowOffset + 'px';
                    top += this.offsetY;
                }

                this.popoverStyle = {top: top + 'px', left: left + 'px'};
                this.arrowStyle = arrowStyle;
            },

            calculateOffset() {
                let top = 0;
                let left = 0;
                let parentRect;

                let parentNode = this.$el.parentElement;
                while (parentNode) {
                    if (matchesSelector(parentNode, '.sticky, .relative-target')) {
                        parentRect = parentNode.getBoundingClientRect();
                        top += parentRect.top;
                        left += parentRect.left;
                    }
                    parentNode = parentNode.parentElement;
                }

                return {left, top};
            },
        },

        watch: {
            async isOpen() {
                if (this.isOpen) {
                    await this.$nextTick();

                    // add click outside handler
                    addClickOutsideHandler(this.close, [...this.autoCloseExcept, this.$el, this.targetNode]);

                    // bind updatePosition
                    window.addEventListener('scroll', this.updatePosition, true);
                    window.addEventListener('resize', this.updatePosition);

                    // update position initially
                    this.updatePosition();
                } else {
                    removeClickOutsideHandler(this.close);
                    window.removeEventListener('scroll', this.updatePosition, true);
                    window.removeEventListener('resize', this.updatePosition);
                }
                this.$emit('toggle', this.isOpen);
            },
        },

        mounted() {
            if (this.autoBind && this.targetNode) {
                this.targetNode.addEventListener('click', this.toggle);
            }
        },

        destroyed() {
            removeClickOutsideHandler(this.close);
            window.removeEventListener('scroll', this.updatePosition, true);
            window.removeEventListener('resize', this.updatePosition);
            if (this.autoBind && this.targetNode) {
                this.targetNode.removeEventListener('click', this.toggle);
            }
        },
    };
</script>
